// Country/Currency Context for Mercapoli Move
import React, { createContext, useContext, useState, useEffect } from 'react';

// Default to Guatemala (GT) with Libras
const DEFAULT_COUNTRY = {
  code: 'GT',
  name: 'Guatemala',
  currency: 'GTQ',
  symbol: 'Q',
  exchange_rate: 7.85, // GTQ per USD
  locale: 'es-GT',
  weight_unit: 'lb',
  distance_unit: 'km',
};

// Unit labels
const UNIT_LABELS = {
  weight: {
    kg: { singular: 'kg', plural: 'kg', name: 'Kilogramos' },
    lb: { singular: 'lb', plural: 'lbs', name: 'Libras' },
  },
  distance: {
    km: { singular: 'km', plural: 'km', name: 'Kilómetros' },
    mi: { singular: 'mi', plural: 'mi', name: 'Millas' },
  },
};

// Conversion factors
const CONVERSIONS = {
  kg_to_lb: 2.20462,
  lb_to_kg: 0.453592,
  km_to_mi: 0.621371,
  mi_to_km: 1.60934,
};

const CountryContext = createContext(null);

export const CountryProvider = ({ children }) => {
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCountries();
  }, []);

  const loadCountries = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/config/countries`);
      const data = await response.json();
      setCountries(data);
      
      // Set default to Guatemala if available
      const gt = data.find(c => c.code === 'GT');
      if (gt) {
        setCountry({
          code: gt.code,
          name: gt.name,
          currency: gt.currency,
          symbol: gt.symbol || 'Q',
          exchange_rate: gt.exchange_rate || 7.85,
          locale: 'es-GT',
          weight_unit: gt.weight_unit || 'lb',
          distance_unit: gt.distance_unit || 'km',
        });
      }
    } catch (error) {
      console.error('Error loading countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchCountry = (countryCode) => {
    const newCountry = countries.find(c => c.code === countryCode);
    if (newCountry) {
      setCountry({
        code: newCountry.code,
        name: newCountry.name,
        currency: newCountry.currency,
        symbol: newCountry.symbol || '$',
        exchange_rate: newCountry.exchange_rate || 1,
        locale: `es-${newCountry.code}`,
        weight_unit: newCountry.weight_unit || 'kg',
        distance_unit: newCountry.distance_unit || 'km',
      });
    }
  };

  // Format currency with symbol
  const formatCurrency = (amount, options = {}) => {
    const { showSymbol = true, decimals = 2 } = options;
    const formatted = Number(amount).toFixed(decimals);
    return showSymbol ? `${country.symbol}${formatted}` : formatted;
  };

  // Convert from USD to local currency
  const convertFromUSD = (usdAmount) => {
    return usdAmount * country.exchange_rate;
  };

  // Convert from local currency to USD
  const convertToUSD = (localAmount) => {
    return localAmount / country.exchange_rate;
  };

  // === WEIGHT UNIT FUNCTIONS ===
  // Get weight unit label
  const getWeightUnit = (plural = false) => {
    const unit = UNIT_LABELS.weight[country.weight_unit] || UNIT_LABELS.weight.kg;
    return plural ? unit.plural : unit.singular;
  };

  // Convert weight to display unit (from kg - internal storage)
  const convertWeightToDisplay = (kgValue) => {
    if (country.weight_unit === 'lb') {
      return kgValue * CONVERSIONS.kg_to_lb;
    }
    return kgValue;
  };

  // Convert weight from display unit to kg (for storage)
  const convertWeightToKg = (displayValue) => {
    if (country.weight_unit === 'lb') {
      return displayValue * CONVERSIONS.lb_to_kg;
    }
    return displayValue;
  };

  // Format weight with unit
  const formatWeight = (kgValue, decimals = 1) => {
    const displayValue = convertWeightToDisplay(kgValue);
    return `${displayValue.toFixed(decimals)} ${getWeightUnit()}`;
  };

  // === DISTANCE UNIT FUNCTIONS ===
  // Get distance unit label
  const getDistanceUnit = (plural = false) => {
    const unit = UNIT_LABELS.distance[country.distance_unit] || UNIT_LABELS.distance.km;
    return plural ? unit.plural : unit.singular;
  };

  // Convert distance to display unit (from km - internal storage)
  const convertDistanceToDisplay = (kmValue) => {
    if (country.distance_unit === 'mi') {
      return kmValue * CONVERSIONS.km_to_mi;
    }
    return kmValue;
  };

  // Convert distance from display unit to km (for storage)
  const convertDistanceToKm = (displayValue) => {
    if (country.distance_unit === 'mi') {
      return displayValue * CONVERSIONS.mi_to_km;
    }
    return displayValue;
  };

  // Format distance with unit
  const formatDistance = (kmValue, decimals = 1) => {
    const displayValue = convertDistanceToDisplay(kmValue);
    return `${displayValue.toFixed(decimals)} ${getDistanceUnit()}`;
  };

  // Get max weight limits for delivery types (in display units)
  const getDeliveryLimits = () => {
    const limits = {
      small_freight: { kg: 20 },
      medium_freight: { kg: 100 },
      large_freight: { kg: 500 },
    };
    
    return Object.entries(limits).reduce((acc, [key, val]) => {
      acc[key] = {
        value: Math.round(convertWeightToDisplay(val.kg)),
        unit: getWeightUnit(),
      };
      return acc;
    }, {});
  };

  return (
    <CountryContext.Provider value={{
      country,
      countries,
      loading,
      switchCountry,
      // Currency
      formatCurrency,
      convertFromUSD,
      convertToUSD,
      // Weight
      getWeightUnit,
      convertWeightToDisplay,
      convertWeightToKg,
      formatWeight,
      // Distance
      getDistanceUnit,
      convertDistanceToDisplay,
      convertDistanceToKm,
      formatDistance,
      // Delivery limits
      getDeliveryLimits,
    }}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
};

export default CountryContext;
